/**
 * AuthResetPassword -- Shared password-reset completion page.
 *
 * Brand differences are supplied through AuthBrandConfig; apps provide the
 * Supabase update callback and navigation target.
 */

import React, { useId, useState } from 'react';
import LiquidGlass from 'liquid-glass-react';
import { AuthSpinner } from './AuthSpinner';
import type { AuthResetPasswordProps } from './types';

const AuthResetPassword: React.FC<AuthResetPasswordProps> = ({
  brand,
  isVerifying,
  onSubmit,
  onNavigateToSignIn,
  onValidationError,
  onSubmitError,
}) => {
  const passwordId = useId();
  const confirmPasswordId = useId();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const gradient = `linear-gradient(180deg, ${brand.gradientFrom} 0%, ${brand.gradientTo} 100%)`;
  const layout = brand.layout ?? {};
  const logoBottomSpacing = layout.logoBottomSpacing ?? 32;
  const titleFontSize = layout.titleFontSize ?? 40;
  const subtitleBottomSpacing = layout.subtitleBottomSpacing ?? 48;
  const cardWidth = layout.cardWidth ?? 331;

  const reportValidationError = (message: string) => {
    if (onValidationError) {
      onValidationError(message);
      return;
    }

    console.error(message);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      reportValidationError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      reportValidationError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await onSubmit(password);
      setIsSubmitted(true);
    } catch (error) {
      if (onSubmitError) {
        onSubmitError(error);
      } else {
        console.error('Password update error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: gradient }}
      >
        {brand.spinner ?? <AuthSpinner brand={brand} size="md" />}
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ background: gradient }}
      >
        <div className="flex flex-col items-center max-w-md w-full">
          <div style={{ marginBottom: `${logoBottomSpacing}px` }}>{brand.logo}</div>

          <h1
            className="mb-2 text-center"
            style={{
              fontFamily: brand.titleFont,
              fontWeight: 400,
              color: brand.titleColor,
              lineHeight: 1.1,
              letterSpacing: '0',
              fontSize: `${titleFontSize}px`,
            }}
          >
            Password Updated
          </h1>

          <p
            className="text-[13px] tracking-[0.08em] uppercase text-center"
            style={{
              color: brand.subtitleColor,
              fontFamily: brand.bodyFont,
              fontWeight: 600,
              marginBottom: `${Math.max(24, subtitleBottomSpacing - 16)}px`,
            }}
          >
            You're all set.
          </p>

          <div
            className="flex flex-col items-center gap-3 w-full"
            style={{
              maxWidth: `${cardWidth}px`,
              padding: '20px 16px',
              borderRadius: '24px',
              background: 'rgba(255, 255, 255, 0.35)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.5)',
            }}
          >
            <button
              type="button"
              onClick={onNavigateToSignIn}
              className="w-full h-11 flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
              style={{
                backgroundColor: brand.buttonBg,
                color: brand.buttonTextColor,
                borderRadius: '10px',
                maxWidth: '303px',
              }}
            >
              <span
                className="text-xs uppercase tracking-wider"
                style={{ fontFamily: brand.bodyFont, fontWeight: 600 }}
              >
                Continue to Sign In
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6"
      style={{ background: gradient }}
    >
      <div className="flex flex-col items-center max-w-md w-full">
        <div style={{ marginBottom: `${logoBottomSpacing}px` }}>{brand.logo}</div>

        <h1
          className="mb-2 text-center"
          style={{
            fontFamily: brand.titleFont,
            fontWeight: 400,
            color: brand.titleColor,
            lineHeight: 1.1,
            letterSpacing: '0',
            fontSize: `${titleFontSize}px`,
          }}
        >
          Set New Password
        </h1>

        <p
          className="text-[13px] tracking-[0.08em] uppercase text-center"
          style={{
            color: brand.subtitleColor,
            fontFamily: brand.bodyFont,
            fontWeight: 600,
            marginBottom: `${subtitleBottomSpacing}px`,
          }}
        >
          Create a secure password.
        </p>

        <div
          className="relative w-full"
          style={{
            maxWidth: `${cardWidth}px`,
            minHeight: '280px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LiquidGlass
            overLight={false}
            cornerRadius={24}
            padding="16px"
            blurAmount={0.08}
            saturation={100}
            displacementScale={18}
            aberrationIntensity={0.5}
            mode="standard"
            elasticity={0.12}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
            }}
          >
            <div className="flex flex-col items-center gap-[15px]" style={{ minWidth: '280px' }}>
              <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3" style={{ maxWidth: '303px' }}>
                <label htmlFor={passwordId} className="sr-only">
                  New password
                </label>
                <input
                  id={passwordId}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="New Password"
                  autoComplete="new-password"
                  className="w-full h-11 px-4 rounded-xl text-sm outline-none transition-all focus:ring-2 focus:ring-black/10"
                  style={{
                    backgroundColor: 'transparent',
                    color: brand.titleColor,
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    fontFamily: brand.bodyFont,
                  }}
                />

                <label htmlFor={confirmPasswordId} className="sr-only">
                  Confirm password
                </label>
                <input
                  id={confirmPasswordId}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm Password"
                  autoComplete="new-password"
                  className="w-full h-11 px-4 rounded-xl text-sm outline-none transition-all focus:ring-2 focus:ring-black/10"
                  style={{
                    backgroundColor: 'transparent',
                    color: brand.titleColor,
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    fontFamily: brand.bodyFont,
                  }}
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-11 flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{
                    backgroundColor: brand.buttonBg,
                    color: brand.buttonTextColor,
                    borderRadius: '10px',
                  }}
                >
                  {isLoading ? (
                    <>{brand.spinnerSm || <AuthSpinner brand={brand} size="sm" />}</>
                  ) : (
                    <span
                      className="text-xs uppercase tracking-wider"
                      style={{ fontFamily: brand.bodyFont, fontWeight: 600 }}
                    >
                      Update Password
                    </span>
                  )}
                </button>
              </form>

              <button
                type="button"
                onClick={onNavigateToSignIn}
                className="w-full h-8 flex items-center justify-center transition-opacity hover:opacity-70"
                style={{
                  backgroundColor: 'transparent',
                  color: brand.linkColor,
                  border: 'none',
                  maxWidth: '303px',
                }}
              >
                <span
                  className="text-xs uppercase tracking-wider"
                  style={{ fontFamily: brand.bodyFont, fontWeight: 600 }}
                >
                  Back to Sign In
                </span>
              </button>
            </div>
          </LiquidGlass>
        </div>
      </div>
    </div>
  );
};

export { AuthResetPassword };
